import { ShoppingCart, Package, ExternalLink, Flame, Clock, Heart, BookOpen, Link2, FileText, Share2, Check, ChevronLeft, ChevronRight } from 'lucide-react'
import type { Product, ProductVariant, User } from '../../../lib/types'
import { useDiscounts } from '../hooks/useDiscounts'
import DOMPurify from 'dompurify'
import { useMemo, useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { buildProductShareLink } from '../utils/productShare'

const normalizeText = (value?: string | null): string => {
  const text = String(value ?? '').trim()
  if (!text) return ''
  const lowered = text.toLowerCase()
  if (lowered === 'null' || lowered === 'undefined') return ''
  return text
}

const ICONS_PER_PAGE = 5

interface ProductCardProps {
  product: Product
  user?: User | null
  onPurchase: (variant: ProductVariant, productName: string) => void
  isFavorite?: boolean
  onToggleFavorite?: (productId: string) => void
  onSelectPackage?: () => void
}

export default function ProductCard({ product, user = null, onPurchase, isFavorite = false, onToggleFavorite, onSelectPackage }: ProductCardProps) {
  const { computePercent, getUnitPrice } = useDiscounts()
  const variantsRef = useRef<HTMLDivElement>(null)

  const productDisplayName = useMemo(() => {
    const byName = normalizeText(product.name)
    if (byName) return byName

    const byCode = normalizeText(product.product_code)
    if (byCode) return byCode

    for (const variant of product.variants || []) {
      const byShortName = normalizeText(variant.short_name)
      if (byShortName) return byShortName
      const byVariantName = normalizeText(variant.name)
      if (byVariantName) return byVariantName
    }

    return 'Sản phẩm'
  }, [product.name, product.product_code, product.variants])

  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsTitle, setDetailsTitle] = useState('')
  const [detailsHtml, setDetailsHtml] = useState('')
  const [copiedShareLink, setCopiedShareLink] = useState(false)
  const [iconPage, setIconPage] = useState(0)

  const sanitizedDetailsHtml = useMemo(() => {
    return DOMPurify.sanitize(detailsHtml || '', { USE_PROFILES: { html: true } })
  }, [detailsHtml])

  const displayVariants = (product.variants || []).filter(v => !v.is_archived)

  const openDetails = (title: string, html: string) => {
    setDetailsTitle(title)
    setDetailsHtml(html)
    setDetailsOpen(true)
  }

  const copyShareLink = async () => {
    try {
      const shareLink = buildProductShareLink(product.id, user?.id || null)
      if (!shareLink) return
      await navigator.clipboard.writeText(shareLink)
      setCopiedShareLink(true)
      window.setTimeout(() => setCopiedShareLink(false), 1800)
    } catch (error) {
      console.error('Không thể copy link sản phẩm:', error)
    }
  }

  const iconButtonBaseClass = 'rounded-full h-10 w-10 flex items-center justify-center transition-colors border'
  const iconActions: Array<{ key: string, node: React.ReactNode }> = [
    {
      key: 'favorite',
      node: (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onToggleFavorite?.(product.id)
          }}
          className={`${iconButtonBaseClass} ${isFavorite
            ? 'bg-rose-600 text-white border-white/20 hover:bg-rose-700'
            : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50 dark:bg-gray-950 dark:text-white dark:border-white/15 dark:hover:bg-gray-900'
            }`}
          title={isFavorite ? 'Bỏ yêu thích' : 'Yêu thích'}
          aria-label={isFavorite ? 'Bỏ yêu thích' : 'Yêu thích'}
        >
          <Heart className="h-5 w-5" fill={isFavorite ? 'currentColor' : 'none'} />
        </button>
      ),
    },
    {
      key: 'package',
      node: (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (onSelectPackage) {
              onSelectPackage()
            } else {
              variantsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
            }
          }}
          className={`${iconButtonBaseClass} bg-amber-600 text-white border-white/20 hover:bg-amber-700`}
          title="Xem gói sản phẩm"
          aria-label="Xem gói sản phẩm"
        >
          <Package className="h-5 w-5" />
        </button>
      ),
    },
  ]

  if (product.description_html) {
    iconActions.push({
      key: 'description',
      node: (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            openDetails(`Chi tiết: ${productDisplayName}`, String(product.description_html || ''))
          }}
          className={`${iconButtonBaseClass} bg-sky-600 text-white border-white/20 hover:bg-sky-700`}
          title="Xem mô tả sản phẩm"
          aria-label="Xem mô tả sản phẩm"
        >
          <FileText className="h-5 w-5" />
        </button>
      ),
    })
  }

  iconActions.push({
    key: 'share',
    node: (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void copyShareLink()
        }}
        className={`${iconButtonBaseClass} bg-cyan-600 text-white border-white/20 hover:bg-cyan-700`}
        title={copiedShareLink ? 'Đã copy link giới thiệu' : 'Tạo link giới thiệu'}
        aria-label={copiedShareLink ? 'Đã copy link giới thiệu' : 'Tạo link giới thiệu'}
      >
        {copiedShareLink ? <Check className="h-5 w-5" /> : <Share2 className="h-5 w-5" />}
      </button>
    ),
  })

  if (product.guide_url) {
    iconActions.push({
      key: 'guide',
      node: (
        <a
          href={product.guide_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className={`${iconButtonBaseClass} bg-emerald-600 text-white border-white/20 hover:bg-emerald-700`}
          title="Xem hướng dẫn"
          aria-label="Xem hướng dẫn"
        >
          <BookOpen className="h-5 w-5" />
        </a>
      ),
    })
  }

  if (product.reference_url) {
    iconActions.push({
      key: 'reference',
      node: (
        <a
          href={product.reference_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className={`${iconButtonBaseClass} bg-purple-600 text-white border-white/20 hover:bg-purple-700`}
          title="Xem tham khảo"
          aria-label="Xem tham khảo"
        >
          <Link2 className="h-5 w-5" />
        </a>
      ),
    })
  }

  const iconPageCount = Math.max(1, Math.ceil(iconActions.length / ICONS_PER_PAGE))
  const clampedIconPage = Math.min(iconPage, iconPageCount - 1)
  const visibleIconActions = iconActions.slice(
    clampedIconPage * ICONS_PER_PAGE,
    clampedIconPage * ICONS_PER_PAGE + ICONS_PER_PAGE
  )

  useEffect(() => {
    if (iconPage !== clampedIconPage) {
      setIconPage(clampedIconPage)
    }
  }, [iconPage, clampedIconPage])

  useEffect(() => {
    setIconPage(0)
  }, [product.id])

  return (
    <>
      <div
        className="card-soft overflow-hidden md:h-[600px] md:min-h-0 flex flex-col md:flex-row shadow-sm hover:shadow-md transition-shadow duration-300"
      >
        {/* Left Column: Image & Info (50%) */}
        <div className="flex flex-col w-full md:w-1/2 md:min-h-0 border-b md:border-b-0 md:border-r border-gray-100 dark:border-white/10">
          {/* Image Area */}
          <div className="relative w-full aspect-video bg-gray-100 dark:bg-slate-950/40 flex-shrink-0 group overflow-hidden">
            <img
              src={product.image_url || 'https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=400'}
              alt={productDisplayName}
              loading="lazy"
              decoding="async"
              width={400}
              height={225}
              className="product-media-image w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            {product.is_hot && (
              <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-md z-10 animate-pulse">
                <Flame className="h-3 w-3" fill="currentColor" />
                HOT
              </div>
            )}
            {product.is_paused && (
              <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                <div className="text-red-600 italic font-extrabold text-lg rotate-[-18deg] bg-white/90 dark:bg-slate-900/90 px-8 py-2.5 border-2 border-red-600 rounded-full shadow-lg tracking-wide">
                  Ngừng bán
                </div>
              </div>
            )}
            {/* Product name overlay on image */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-3 pt-6 pb-2 pointer-events-none">
              <h3 className="text-white text-base font-bold line-clamp-1 drop-shadow-lg" title={productDisplayName}>{productDisplayName}</h3>
            </div>
          </div>

          {/* Info Content */}
          <div className="p-3 flex flex-col flex-none md:flex-1 md:min-h-0 md:overflow-y-auto custom-scrollbar">

            {/* Info */}
            <div className="space-y-2 flex-1">
              {product.mechanism && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-0.5">Cơ chế:</p>
                  <p className="text-sm text-gray-600 dark:text-slate-300 whitespace-pre-wrap break-words">{product.mechanism}</p>
                </div>
              )}

              {product.recommended_model && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-0.5">Model khuyến dùng:</p>
                  <p className="text-sm text-amber-600 font-medium">{product.recommended_model}</p>
                </div>
              )}

              {product.strengths && (
                <div>
                  <p className="text-sm font-semibold text-green-700 dark:text-emerald-300 mb-0.5">Điểm mạnh:</p>
                  <p className="text-sm text-gray-600 dark:text-slate-300 whitespace-pre-wrap break-words">{product.strengths}</p>
                </div>
              )}

              {product.weaknesses && (
                <div>
                  <p className="text-sm font-semibold text-red-700 dark:text-rose-300 mb-0.5">Điểm yếu:</p>
                  <p className="text-sm text-gray-600 dark:text-slate-300 whitespace-pre-wrap break-words">{product.weaknesses}</p>
                </div>
              )}

              {!!product.description_html && (
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      openDetails(`Chi tiết: ${productDisplayName}`, String(product.description_html || ''))
                    }}
                    className="inline-flex items-center justify-center px-3 py-1 rounded-md border border-gray-300 dark:border-white/10 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-white/10 text-sm font-semibold transition-transform duration-150 hover:scale-105 active:scale-95"
                  >
                    Chi tiết
                  </button>
                </div>
              )}

              {product.guide_url && (
                <div className="pt-1.5">
                  <a
                    href={product.guide_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-amber-600 hover:underline flex items-center gap-1"
                  >
                    Xem hướng dẫn <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}

              <div className="pt-1">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    void copyShareLink()
                  }}
                  className="text-sm text-cyan-700 hover:underline flex items-center gap-1 dark:text-cyan-300"
                >
                  {copiedShareLink ? <Check className="h-3 w-3" /> : <Share2 className="h-3 w-3" />}
                  {copiedShareLink ? 'Đã copy link giới thiệu' : 'Tạo link giới thiệu sản phẩm'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Variants (50% on XL) */}
        <div ref={variantsRef} className="w-full md:w-1/2 md:min-h-0 flex flex-col bg-gray-50/50 dark:bg-slate-950/20">
          <div className="p-2.5 pb-2 bg-white dark:bg-slate-950/40 border-b border-gray-200 dark:border-white/10">
            <div className="min-h-[56px] flex items-center justify-center gap-2.5">
              {iconPageCount > 1 && (
                <button
                  type="button"
                  onClick={() => setIconPage((prev) => Math.max(0, prev - 1))}
                  disabled={clampedIconPage === 0}
                  aria-label="Trang icon trước"
                  className={`h-8 w-8 rounded-full border flex items-center justify-center transition-colors ${clampedIconPage === 0
                    ? 'cursor-not-allowed text-gray-300 border-gray-200 dark:text-slate-600 dark:border-white/10'
                    : 'text-gray-700 border-gray-300 hover:bg-gray-100 dark:text-slate-200 dark:border-white/15 dark:hover:bg-white/10'
                    }`}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              )}

              <div className="flex items-center justify-center gap-2.5 flex-nowrap">
                {visibleIconActions.map((action) => (
                  <div
                    key={action.key}
                    className="rounded-full p-[3px] bg-gradient-to-r from-pink-500 via-yellow-400 to-cyan-400 shadow-[0_8px_25px_rgba(0,0,0,0.25)] transition-transform duration-150 hover:scale-110 active:scale-95"
                  >
                    {action.node}
                  </div>
                ))}
              </div>

              {iconPageCount > 1 && (
                <button
                  type="button"
                  onClick={() => setIconPage((prev) => Math.min(iconPageCount - 1, prev + 1))}
                  disabled={clampedIconPage >= iconPageCount - 1}
                  aria-label="Trang icon sau"
                  className={`h-8 w-8 rounded-full border flex items-center justify-center transition-colors ${clampedIconPage >= iconPageCount - 1
                    ? 'cursor-not-allowed text-gray-300 border-gray-200 dark:text-slate-600 dark:border-white/10'
                    : 'text-gray-700 border-gray-300 hover:bg-gray-100 dark:text-slate-200 dark:border-white/15 dark:hover:bg-white/10'
                    }`}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="p-2.5 bg-white dark:bg-slate-950/40 border-b border-gray-200 dark:border-white/10">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-bold text-gray-700 dark:text-slate-200 flex items-center gap-2">
                <Package className="h-4 w-4 text-amber-600" />
                Chọn gói ({displayVariants.length || 0})
              </p>
              {product.product_code ? (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-slate-200 border border-gray-200 dark:border-white/10 max-w-[45%] truncate">
                  {product.product_code}
                </span>
              ) : null}
            </div>
          </div>

          <div className="p-2.5 space-y-2.5 flex-none md:flex-1 md:min-h-0 md:overflow-y-auto custom-scrollbar">
            {displayVariants.map((variant) => {
              const isUnlimitedStock = !!variant.is_infinity || variant.stock === null
              const effectiveStock = isUnlimitedStock
                ? Number.POSITIVE_INFINITY
                : (variant.stock ?? 0)
              const effectiveStockLabel = isUnlimitedStock ? '∞' : String(effectiveStock)

              const isVariantPaused = !!variant.is_paused
              const descriptionText = String(variant.description || '').trim()
              const descriptionHtmlText = String(variant.description_html || '').trim()
              const hasVariantExtraContent = !!descriptionText || !!descriptionHtmlText
              const durationLabel = (variant.duration_days || 0) === 0 ? 'Trọn đời' : `${variant.duration_days} ngày`
              const warrantyLabel = (() => {
                const durationDays = variant.duration_days ?? 0
                const fallback = durationDays > 0 ? `${durationDays} ngày` : 'Trọn gói'
                if (variant.warranty_mode === 'none') return 'Không'
                if (variant.warranty_mode === 'full') return 'Trọn gói'
                const days = variant.warranty_days
                if (days == null) return fallback
                if (days > 0) return `${days} ngày`
                return 'Không'
              })()

              const variantDisplayName = variant.name?.trim() || durationLabel

              return (
                <div
                  key={variant.id}
                  className={`card-soft dark:bg-slate-950/35 p-2.5 group relative flex flex-col transition-transform duration-150 hover:scale-[1.01] ${hasVariantExtraContent ? 'min-h-[180px]' : ''}`}
                >
                  {/* Variant Header - Name Only */}
                  <div className="mb-2">
                    <span className="font-bold text-gray-900 dark:text-slate-100 text-sm whitespace-normal break-words leading-snug" title={variantDisplayName}>
                      {variantDisplayName}
                    </span>
                  </div>

                  {/* Info Grid - Excel Style */}
                  <div className="space-y-1.5 mb-3">
                    <div className="flex items-start justify-between gap-2 text-sm">
                      <span className="text-gray-600 dark:text-slate-300 flex items-center gap-1 whitespace-nowrap">
                        <Clock className="h-3 w-3" />
                        Thời lượng:
                      </span>
                      <span className="text-gray-900 dark:text-slate-100 font-semibold text-right break-words">{durationLabel}</span>
                    </div>

                    <div className="flex items-start justify-between gap-2 text-sm">
                      <span className="text-gray-600 dark:text-slate-300 flex items-center gap-1 whitespace-nowrap">
                        🛡️ Bảo hành:
                      </span>
                      <span className="text-gray-900 dark:text-slate-100 font-semibold text-right break-words">{warrantyLabel}</span>
                    </div>

                    <div className="flex items-start justify-between gap-2 text-sm">
                      <span className="text-gray-600 dark:text-slate-300 whitespace-nowrap">Tồn kho:</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-900 dark:text-slate-100 font-semibold">{effectiveStockLabel}</span>
                        {effectiveStock <= 0 ? (
                          <span className="text-xs bg-red-100 dark:bg-rose-500/15 text-red-600 dark:text-rose-200 px-1.5 py-0.5 rounded-full font-bold uppercase">
                            Hết
                          </span>
                        ) : isVariantPaused ? (
                          <span className="text-xs bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-slate-200 px-1.5 py-0.5 rounded-full font-bold uppercase">
                            Dừng
                          </span>
                        ) : effectiveStock < 5 ? (
                          <span className="text-xs bg-yellow-100 dark:bg-amber-500/15 text-yellow-800 dark:text-amber-200 px-1.5 py-0.5 rounded-full font-bold">
                            Ít
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex items-start justify-between gap-2 text-sm">
                      <span className="text-gray-600 dark:text-slate-300 whitespace-nowrap">Đã bán:</span>
                      <span className="text-gray-900 dark:text-slate-100 font-semibold">{variant.total_sold || 0}</span>
                    </div>

                    {/* Row 3: Giá trước khi giảm | Giá bán */}
                    <div className="flex justify-between items-center pt-1.5 border-t border-gray-100 dark:border-white/5">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-400">Giá trước khi giảm:</span>
                        {getUnitPrice(user, variant) < (variant.price || 0) ? (
                          <span className="text-sm text-gray-400 line-through">
                            {(variant.price || 0).toLocaleString('vi-VN')}đ
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-xs text-gray-500 dark:text-slate-400 font-medium">Giá bán:</span>
                        <div className="flex items-center gap-1.5">
                          {(() => {
                            const { totalPercentAppliedToPrice } = computePercent(user, variant)
                            return totalPercentAppliedToPrice > 0 ? (
                              <span className="text-xs bg-red-50 text-red-600 px-1 py-0.5 rounded border border-red-100 font-bold">
                                -{totalPercentAppliedToPrice}%
                              </span>
                            ) : null
                          })()}
                          <span className={`text-base font-extrabold ${getUnitPrice(user, variant) < (variant.price || 0) ? 'text-red-600' : 'text-amber-600'}`}>
                            {(getUnitPrice(user, variant) || (variant.price || 0)).toLocaleString('vi-VN')}đ
                          </span>
                        </div>
                        {variant.duration_days && variant.duration_days > 0 && (
                          <span className="text-xs text-gray-400 dark:text-slate-500">
                            {Math.round((getUnitPrice(user, variant) || variant.price || 0) / variant.duration_days).toLocaleString('vi-VN')}đ/ngày
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Variant Description */}
                  {variant.description && (
                    <div className="mb-3">
                      <span className="text-sm text-gray-600 dark:text-slate-300 line-clamp-2 italic" title={variant.description}>
                        {variant.description}
                      </span>
                    </div>
                  )}

                  {/* Action Buttons Area */}
                  <div className="mt-auto">
                    {/* Action Buttons - Full Width */}
                    <div className="flex flex-col gap-1.5 w-full">
                      {!!variant.description_html && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            openDetails(`Chi tiết gói: ${variant.name}`, String(variant.description_html || ''))
                          }}
                          className="neon-control border w-full py-2 rounded-md text-sm font-bold uppercase tracking-wider transition-all shadow-sm bg-white dark:bg-slate-950/40 text-gray-800 dark:text-slate-100 hover:bg-gray-50 dark:hover:bg-slate-950/55 hover:scale-[1.02] active:scale-[0.98]"
                        >
                          Chi tiết
                        </button>
                      )}

                      <button
                        onClick={() => onPurchase(variant, productDisplayName)}
                        disabled={effectiveStock <= 0 || !!product.is_paused || isVariantPaused}
                        title={
                          product.is_paused ? 'Sản phẩm đang tạm dừng bán' :
                            isVariantPaused ? 'Gói này đang tạm dừng bán' :
                              effectiveStock <= 0 ? 'Hết hàng' : undefined
                        }
                        className={`neon-control border w-full py-2.5 rounded-md text-sm font-bold uppercase tracking-wider transition-all shadow-sm flex items-center justify-center gap-2 ${(effectiveStock <= 0 || !!product.is_paused || isVariantPaused)
                          ? 'bg-gray-100 dark:bg-white/10 text-gray-400 dark:text-slate-500 cursor-not-allowed'
                          : 'bg-amber-600 text-white hover:bg-amber-700 hover:shadow-md hover:scale-[1.02] active:scale-[0.98]'
                          }`}
                      >
                        <ShoppingCart className="h-4 w-4" />
                        {product.is_paused || isVariantPaused ? 'Tạm dừng' : 'Mua ngay'}
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}

            {displayVariants.length === 0 && (
              <div className="text-center py-8 text-gray-400 text-sm italic">
                Đang cập nhật gói sản phẩm...
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {detailsOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center p-4 pb-[env(safe-area-inset-bottom,0.5rem)] overflow-y-auto z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-900 rounded-xl w-full max-w-3xl my-auto max-h-[85vh] sm:max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-800 shadow-lg"
            >
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="text-base font-semibold text-gray-900 dark:text-gray-100">{detailsTitle}</div>
                  <button
                    type="button"
                    onClick={() => setDetailsOpen(false)}
                    className="px-3 py-2 rounded-md border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm font-semibold"
                  >
                    Đóng
                  </button>
                </div>

                <div
                  className="prose prose-sm max-w-none dark:prose-invert"
                  dangerouslySetInnerHTML={{ __html: sanitizedDetailsHtml }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
